import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Download, Filter, TrendingUp, TrendingDown, AlertTriangle, Check } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { AsanaTask, getAsanaTasks } from "../services/asanaService";
import { getAggregatedL7Data } from "../services/organizationS3Service";
import "./RoadmapAnalytics.css";

interface RoadmapAnalyticsProps {
  tasks: AsanaTask[];
}

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#7C3AED', '#06B6D4', '#EC4899', '#14B8A6'];

const formatCompactCurrency = (value: number) => {
  if (!isFinite(value) || value === 0) return '$0';
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const formatNumber = (value: number) => {
  if (!isFinite(value)) return '0.0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(value);
};

const downloadCSV = async (filename: string, rows: Record<string, any>[], setExporting: (loading: boolean) => void) => {
  if (!rows || rows.length === 0) return;
  
  setExporting(true);
  
  try {
    const keys = Array.from(
      rows.reduce<Set<string>>((acc, r) => {
        Object.keys(r).forEach((k) => acc.add(k));
        return acc;
      }, new Set<string>())
    );
    
    const csv = [
      keys.join(","),
      ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setExporting(false);
  }
};


interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div ref={ref} className="dropdown-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="dropdown-button"
      >
        <span className="dropdown-button-text">
          {selected.length === 0 ? label : `${label} (${selected.length})`}
        </span>
        <Filter size={14} />
      </button>
      
      {isOpen && (
        <div className="dropdown-menu">
          {options.map(opt => (
            <label key={opt} className="dropdown-option">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggleOption(opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const RoadmapAnalytics: React.FC<RoadmapAnalyticsProps> = ({ tasks }) => {
  // Routing hooks
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [allTasks, setAllTasks] = useState<AsanaTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  
  // Helper function to get P1/P2 decisions dynamically
  const getP1P2Decisions = (tasks: AsanaTask[]): string[] => {
    const allDecisions = Array.from(new Set(tasks.map(task => (task as any).op_decision))).filter(Boolean);
    return allDecisions.filter(decision => {
      const firstWord = decision.split(' ')[0] || '';
      return firstWord === 'P1' || firstWord === 'P2';
    });
  };

  // Multi-select filters - Set defaults for current year and P1/P2 decisions
  const [filters, setFilters] = useState({
    search: '',
    l7_managers: [] as string[],
    programs: [] as string[],
    op_decisions: [] as string[],
    priorities: [] as string[],
    mission_fits: [] as string[],
    impact_metrics: [] as string[],
    target_years: [new Date().getFullYear().toString()] as string[], // Default to current year
    request_types: [] as string[],
  });
  
  const [activeView, setActiveView] = useState<'program' | 'l7' | 'theme' | 'efficiency' | 'operations'>('program');
  const [showFilters, setShowFilters] = useState(true);
  const [organizationData, setOrganizationData] = useState<any>(null);

  useEffect(() => {
    // Use provided tasks directly
    setAllTasks(tasks);
    setIsLoading(false);
  }, [tasks]);

  // Set default P1/P2 decision filters when tasks are loaded - similar to TaskPrioritization
  useEffect(() => {
    if (allTasks.length > 0) {
      const p1p2Decisions = getP1P2Decisions(allTasks);
      
      // Only set P1/P2 defaults if no op_decisions filter is currently active
      if (filters.op_decisions.length === 0 && p1p2Decisions.length > 0) {
        setFilters(prev => ({
          ...prev,
          op_decisions: p1p2Decisions
        }));
      }
    }
  }, [allTasks.length]); // Only run when tasks are loaded

  // Load organization data when L7 filters change
  useEffect(() => {
    const loadOrganizationData = async () => {
      try {
        // Filter organization data by year if year filters are active
        const orgData = await getAggregatedL7Data(
          filters.l7_managers.length > 0 ? filters.l7_managers : [],
          // Get organization ID from tasks if available
          allTasks.length > 0 ? (allTasks[0] as any).organizationId : undefined
        );
        
        // Apply year filtering to organization data if year filters are active
        if (orgData && orgData.matchedManagers && filters.target_years.length > 0) {
          // Filter the organization data based on tasks that match the year filter
          const yearFilteredTasks = allTasks.filter(t => {
            const targetYearValue = (t as any).target_year || 'Not set';
            return filters.target_years.includes(targetYearValue);
          });
          
          // Get L7 managers from year-filtered tasks
          const yearFilteredL7Managers = Array.from(new Set(
            yearFilteredTasks.map(t => (t as any).l7_sdm || (t as any).l7_manager).filter(Boolean)
          ));
          
          // Filter organization managers to only include those with tasks in the selected years
          const filteredManagers = orgData.matchedManagers.filter((mgr: any) => 
            yearFilteredL7Managers.includes(mgr.name)
          );
          
          setOrganizationData({
            ...orgData,
            matchedManagers: filteredManagers
          });
        } else {
          setOrganizationData(orgData);
        }
     
      } catch (error) {
        console.error('🔧 SERVICE OPERATIONS ERROR: Failed to load organization data:', error);
        setOrganizationData(null);
      }
    };

    if (activeView === 'operations') {
      loadOrganizationData();
    }
  }, [filters.l7_managers, filters.target_years, activeView, allTasks]);

  // Unique values for filters
  const programs = useMemo(() => 
    Array.from(new Set(allTasks.map(t => (t as any).program || 'Unassigned'))).sort(), 
    [allTasks]
  );
  
  // L7 Managers should be based on L7 SDMs instead of being empty
  const l7s = useMemo(() => {
    const l7Values = Array.from(new Set(allTasks.map(t => (t as any).l7_sdm || (t as any).l7_manager || 'Unknown'))).filter(v => v !== 'Unknown').sort();
 
    return l7Values.length > 0 ? l7Values : ['Unknown'];
  }, [allTasks]);
  
  const opDecisions = useMemo(() => 
    Array.from(new Set(allTasks.map(t => (t as any).op_decision || 'Not set'))).sort(), 
    [allTasks]
  );
  
  const priorities = useMemo(() => 
    Array.from(new Set(allTasks.map(t => t.priority || 'Unset'))).sort(), 
    [allTasks]
  );
  
  // Replace themes with mission fit
  const missionFits = useMemo(() => 
    Array.from(new Set(allTasks.map(t => (t as any).mission_fit || 'Unassigned'))).sort(), 
    [allTasks]
  );

  // Add Impact Metric Filter
  const impactMetrics = useMemo(() => 
    Array.from(new Set(allTasks.map(t => (t as any).impact_metrics || 'Not set'))).sort(), 
    [allTasks]
  );

  // Add Target Year Filter
  const targetYears = useMemo(() => 
    Array.from(new Set(allTasks.map(t => (t as any).target_year || 'Not set'))).sort(), 
    [allTasks]
  );

  // Add Request Type Filter
  const requestTypes = useMemo(() => 
    Array.from(new Set(allTasks.map(t => (t as any).request_type || 'Not set'))).sort(), 
    [allTasks]
  );

  // Apply filters
  const filteredTasks = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    

    
    const result = allTasks.filter(t => {
      // Debug individual filter checks
      const l7Value = (t as any).l7_sdm || (t as any).l7_manager || 'Unknown';
      const programValue = (t as any).program || 'Unassigned';
      const opDecisionValue = (t as any).op_decision || 'Not set';
      const priorityValue = t.priority || 'Unset';
      const missionFitValue = (t as any).mission_fit || 'Unassigned';
      const impactMetricValue = (t as any).impact_metrics || 'Not set';
      
      // L7 SDM filter
      if (filters.l7_managers.length > 0) {
        const l7Match = filters.l7_managers.includes(l7Value);
        if (!l7Match) {
        
          return false;
        }
      }
      
      // Program filter
      if (filters.programs.length > 0) {
        const programMatch = filters.programs.includes(programValue);
        if (!programMatch) {
   
          return false;
        }
      }
      
      // Op Decision filter
      if (filters.op_decisions.length > 0) {
        const opMatch = filters.op_decisions.includes(opDecisionValue);
        if (!opMatch) {
      
          return false;
        }
      }
      
      // Priority filter
      if (filters.priorities.length > 0) {
        const priorityMatch = filters.priorities.includes(priorityValue);
        if (!priorityMatch) {

          return false;
        }
      }
      
      // Mission Fit filter
      if (filters.mission_fits.length > 0) {
        const missionMatch = filters.mission_fits.includes(missionFitValue);
        if (!missionMatch) {

          return false;
        }
      }
      
      // Impact Metrics filter
      if (filters.impact_metrics.length > 0) {
        const impactMatch = filters.impact_metrics.includes(impactMetricValue);
        if (!impactMatch) {
          
          return false;
        }
      }
      
      // Target Year filter
      const targetYearValue = (t as any).target_year || 'Not set';
      if (filters.target_years.length > 0) {
        const yearMatch = filters.target_years.includes(targetYearValue);
        if (!yearMatch) {
        
          return false;
        }
      }
      
      // Request Type filter
      const requestTypeValue = (t as any).request_type || 'Not set';
      if (filters.request_types.length > 0) {
        const typeMatch = filters.request_types.includes(requestTypeValue);
        if (!typeMatch) {
        
          return false;
        }
      }
      
      // Search filter
      if (s) {
        const hay = [t.name, programValue, missionFitValue, l7Value, opDecisionValue]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(s)) {
        
          return false;
        }
      }
      
      return true;
    });
    
  
    return result;
  }, [allTasks, filters]);

  // Check if op_decision filters are active
  const hasOpDecisionFilter = useMemo(() => filters.op_decisions.length > 0, [filters.op_decisions]);

  // Program-based metrics and health score
  const metrics = useMemo(() => {
    const totalEnt = filteredTasks.reduce((acc, t) => acc + (parseFloat((t as any).annualized_entitlement) || 0), 0);
    const totalSDE = filteredTasks.reduce((acc, t) => acc + (parseFloat((t as any).hc_sde) || 0), 0);
    const totalTasks = filteredTasks.length;
    
    const avgEntPerTask = totalTasks > 0 ? totalEnt / totalTasks : 0;
    const avgSDEPerTask = totalTasks > 0 ? totalSDE / totalTasks : 0;
    const entPerSDE = totalSDE > 0 ? totalEnt / totalSDE : 0;

    // Group tasks by program
    const programMap = new Map<string, any>();
    
    filteredTasks.forEach(task => {
      const programName = (task as any).program || 'Unassigned';
      const ent = parseFloat((task as any).annualized_entitlement) || 0;
      const sde = parseFloat((task as any).hc_sde) || 0;
      const opDecision = (task as any).op_decision || '';
      
      if (!programMap.has(programName)) {
        programMap.set(programName, {
          name: programName,
          totalEnt: 0,
          totalSDE: 0,
          taskCount: 0,
          p1Ent: 0,
          p2Ent: 0,
          btlEnt: 0,
          p1SDE: 0,
          p2SDE: 0,
          btlSDE: 0,
          l7Managers: new Set(),
          themes: new Set(),
          tasks: []
        });
      }
      
      const program = programMap.get(programName)!;
      program.totalEnt += ent;
      program.totalSDE += sde;
      program.taskCount += 1;
      program.tasks.push(task);
      
      if ((task as any).l7_manager) program.l7Managers.add((task as any).l7_manager);
      if ((task as any).theme) program.themes.add((task as any).theme);
      
      if (/P1/i.test(opDecision)) {
        program.p1Ent += ent;
        program.p1SDE += sde;
      } else if (/P2/i.test(opDecision)) {
        program.p2Ent += ent;
        program.p2SDE += sde;
      } else {
        program.btlEnt += ent;
        program.btlSDE += sde;
      }
    });

    const programs = Array.from(programMap.values()).map(program => {
      const p1Ratio = program.totalEnt > 0 ? program.p1Ent / program.totalEnt : 0;
      const strategicPriority = program.totalEnt > 0 ? 
        (p1Ratio > 0.5 ? 'P1' : 
         p1Ratio > 0.2 ? 'P2' : 'BTL') : 'BTL';
      

      
      return {
        ...program,
        l7ManagerCount: program.l7Managers.size,
        themeCount: program.themes.size,
        hasL7Owner: program.l7Managers.size > 0,
        hasCoherentTheme: program.themeCount <= 2, // Programs should focus on 1-2 themes
        strategicPriority: strategicPriority
      };
    });

    // Program-based health score calculation
    const programCount = programs.length;
    const p1Programs = programs.filter(p => p.strategicPriority === 'P1');
    const p2Programs = programs.filter(p => p.strategicPriority === 'P2');
    const btlPrograms = programs.filter(p => p.strategicPriority === 'BTL');



    // 1. Strategic Alignment Score (40% weight)
    // When op_priority filters are active, calculate from filtered tasks instead of program classifications
    let p1ProgramEntitlement = 0;
    let p2ProgramEntitlement = 0;
    let strategicEntitlement = 0;
    
    if (hasOpDecisionFilter) {
      // Calculate entitlements based on filtered tasks (respects op_decision filter)
      const p1FilteredTasks = filteredTasks.filter(t => /P1/i.test((t as any).op_decision || ''));
      const p2FilteredTasks = filteredTasks.filter(t => /P2/i.test((t as any).op_decision || ''));
      
      p1ProgramEntitlement = p1FilteredTasks.reduce((sum, t) => sum + (parseFloat((t as any).annualized_entitlement) || 0), 0);
      p2ProgramEntitlement = p2FilteredTasks.reduce((sum, t) => sum + (parseFloat((t as any).annualized_entitlement) || 0), 0);
      strategicEntitlement = p1ProgramEntitlement + p2ProgramEntitlement;
    } else {
      // Use program-level classifications when no op_decision filter is active
      p1ProgramEntitlement = p1Programs.reduce((sum, p) => sum + p.totalEnt, 0);
      p2ProgramEntitlement = p2Programs.reduce((sum, p) => sum + p.totalEnt, 0);
      strategicEntitlement = p1ProgramEntitlement + p2ProgramEntitlement;
    }
    

    
    // Target: P1 should be 80% of strategic investments, P2 should be 20%
    const p1Ratio = strategicEntitlement > 0 ? (p1ProgramEntitlement / strategicEntitlement) : 0;
    const strategicScore = strategicEntitlement > 0 ? (p1Ratio * 100) : 0;

    // 2. Program Portfolio Balance (35% weight)
    const avgProgramSize = programCount > 0 ? totalSDE / programCount : 0;
    const idealProgramSizeRange = { min: 5, max: 15 }; // SDE per program
    
    let balanceScore = 0;
    if (programCount > 0) {
      if (avgProgramSize >= idealProgramSizeRange.min && avgProgramSize <= idealProgramSizeRange.max) {
        balanceScore = 100;
      } else if (avgProgramSize < idealProgramSizeRange.min) {
        balanceScore = (avgProgramSize / idealProgramSizeRange.min) * 100;
      } else {
        balanceScore = (idealProgramSizeRange.max / avgProgramSize) * 100;
      }
    }

    // 3. Mission Fit Score (25% weight) - Replace Execution Readiness
    // Calculate Mission Fit: if 90% $$ are coming from Mission Fit
    const missionFitTasks = filteredTasks.filter(t => (t as any).mission_fit && (t as any).mission_fit !== 'Unassigned');
    const missionFitEntitlement = missionFitTasks.reduce((sum, t) => sum + (parseFloat((t as any).annualized_entitlement) || 0), 0);
    const missionFitRatio = totalEnt > 0 ? (missionFitEntitlement / totalEnt) : 0;
    const missionFitScore = missionFitRatio * 100; // Score based on percentage of entitlement from mission fit

    // Final Program Health Score - Updated formula
    const programHealthScore = Math.round(
      (strategicScore * 0.40) + 
      (balanceScore * 0.35) + 
      (missionFitScore * 0.25)
    );

    // Legacy task-based metrics for compatibility
    const p1Tasks = filteredTasks.filter(t => /P1/i.test((t as any).op_decision || ''));
    const p2Tasks = filteredTasks.filter(t => /P2/i.test((t as any).op_decision || ''));
    const btlTasks = filteredTasks.filter(t => /BTL|P3/i.test((t as any).op_decision || ''));

    const p1Ent = p1Tasks.reduce((acc, t) => acc + (parseFloat((t as any).annualized_entitlement) || 0), 0);
    const p2Ent = p2Tasks.reduce((acc, t) => acc + (parseFloat((t as any).annualized_entitlement) || 0), 0);
    const btlEnt = btlTasks.reduce((acc, t) => acc + (parseFloat((t as any).annualized_entitlement) || 0), 0);

    const p1SDE = p1Tasks.reduce((acc, t) => acc + (parseFloat((t as any).hc_sde) || 0), 0);
    const p2SDE = p2Tasks.reduce((acc, t) => acc + (parseFloat((t as any).hc_sde) || 0), 0);
    const btlSDE = btlTasks.reduce((acc, t) => acc + (parseFloat((t as any).hc_sde) || 0), 0);

    return {
      totalEnt,
      totalSDE,
      totalTasks,
      avgEntPerTask,
      avgSDEPerTask,
      entPerSDE,
      // Legacy task metrics
      p1: { count: p1Tasks.length, ent: p1Ent, sde: p1SDE, pct: totalEnt > 0 ? (p1Ent / totalEnt) * 100 : 0 },
      p2: { count: p2Tasks.length, ent: p2Ent, sde: p2SDE, pct: totalEnt > 0 ? (p2Ent / totalEnt) * 100 : 0 },
      btl: { count: btlTasks.length, ent: btlEnt, sde: btlSDE, pct: totalEnt > 0 ? (btlEnt / totalEnt) * 100 : 0 },
      // New program-based metrics
      programs,
      programCount,
      avgProgramSize,
      p1Programs: { count: p1Programs.length, ent: p1ProgramEntitlement, programs: p1Programs },
      p2Programs: { count: p2Programs.length, ent: p2ProgramEntitlement, programs: p2Programs },
      btlPrograms: { count: btlPrograms.length, ent: btlPrograms.reduce((sum, p) => sum + p.totalEnt, 0), programs: btlPrograms },
      // Health score components
      strategicScore,
      balanceScore,
      missionFitScore,
      missionFitRatio,
      missionFitEntitlement,
      missionFitTasks: { count: missionFitTasks.length, ent: missionFitEntitlement, sde: missionFitTasks.reduce((sum, t) => sum + (parseFloat((t as any).hc_sde) || 0), 0) },
      healthScore: programHealthScore,
    };
  }, [filteredTasks]);

  const aggregateBy = (key: string) => {
    const map = new Map<string, any>();
    
    for (const t of filteredTasks) {
      const k = (t as any)[key] || 'Unassigned';
      const ent = parseFloat((t as any).annualized_entitlement) || 0;
      const sde = parseFloat((t as any).hc_sde) || 0;
      
      if (!map.has(k)) map.set(k, { key: k, totalEnt: 0, totalSDE: 0, count: 0, p1: 0, p2: 0, btl: 0 });
      
      const cur = map.get(k)!;
      cur.totalEnt += ent;
      cur.totalSDE += sde;
      cur.count += 1;
      
      const op = (t as any).op_decision || '';
      if (/P1/i.test(op)) cur.p1 += sde;
      else if (/P2/i.test(op)) cur.p2 += sde;
      else if (/BTL|P3/i.test(op)) cur.btl += sde;
      else cur.btl += sde;
    }
    
    return Array.from(map.values()).map(r => ({
      ...r,
      entitlementPerSDE: r.totalSDE > 0 ? r.totalEnt / r.totalSDE : 0,
      pctOfTotal: metrics.totalEnt > 0 ? (r.totalEnt / metrics.totalEnt) * 100 : 0,
      efficiency: r.count > 0 ? r.totalEnt / r.count : 0,
    }));
  };

  const byProgram = useMemo(() => aggregateBy('program'), [filteredTasks, metrics.totalEnt]);
  const byL7 = useMemo(() => {
    // Debug L7 manager field mapping

    
    // Use l7_sdm field instead of l7_manager for aggregation
    return aggregateBy('l7_sdm');
  }, [filteredTasks, metrics.totalEnt]);
  const byTheme = useMemo(() => aggregateBy('theme'), [filteredTasks, metrics.totalEnt]);

  // Efficiency analysis
  const efficiencyData = useMemo(() => {
    return filteredTasks.map(t => ({
      name: t.name,
      entitlement: parseFloat((t as any).annualized_entitlement) || 0,
      sde: parseFloat((t as any).hc_sde) || 0,
      efficiency: (parseFloat((t as any).hc_sde) || 0) > 0 ? 
        (parseFloat((t as any).annualized_entitlement) || 0) / (parseFloat((t as any).hc_sde) || 0) : 0,
      program: (t as any).program,
    })).filter(d => d.sde > 0 && d.entitlement > 0);
  }, [filteredTasks]);

  // Portfolio balance
  const portfolioData = useMemo(() => [
    { name: 'P1 Critical', value: metrics.p1.sde, count: metrics.p1.count, fill: COLORS[3] },
    { name: 'P2 Important', value: metrics.p2.sde, count: metrics.p2.count, fill: COLORS[2] },
    { name: 'BTL Backlog', value: metrics.btl.sde, count: metrics.btl.count, fill: COLORS[4] },
  ], [metrics]);

  // Radar chart for program balance
  const radarData = useMemo(() => {
    const top6 = byProgram.sort((a, b) => b.totalSDE - a.totalSDE).slice(0, 6);
    return top6.map(p => ({
      program: p.key,
      sde: p.totalSDE,
      tasks: p.count,
    }));
  }, [byProgram]);

  // Impact metrics data for chart
  const impactMetricsData = useMemo(() => {
    const impactMap = new Map<string, any>();
    
    filteredTasks.forEach(task => {
      const impactMetric = (task as any).impact_metrics || 'Not set';
      const ent = parseFloat((task as any).annualized_entitlement) || 0;
      const sde = parseFloat((task as any).hc_sde) || 0;
      
      if (!impactMap.has(impactMetric)) {
        impactMap.set(impactMetric, {
          name: impactMetric,
          entitlement: 0,
          sde: 0,
          count: 0
        });
      }
      
      const current = impactMap.get(impactMetric)!;
      current.entitlement += ent;
      current.sde += sde;
      current.count += 1;
    });
    
    return Array.from(impactMap.values())
      .filter(d => d.entitlement > 0)
      .sort((a, b) => b.entitlement - a.entitlement)
      .slice(0, 8); // Top 8 impact metrics
  }, [filteredTasks]);

  // Service Operations & KTLOR Analysis Data - Only use real organization data
  const serviceOperationsData = useMemo(() => {
   
    // Only process if we have real organization data with service information
    if (organizationData && organizationData.matchedManagers && organizationData.matchedManagers.length > 0) {
      // Filter managers that have actual service data (not zeros)
      const managersWithRealServiceData = organizationData.matchedManagers.filter((mgr: any) => 
        (mgr.tier1Service && mgr.tier1Service > 0) || (mgr.tier2Service && mgr.tier2Service > 0)
      );

      if (managersWithRealServiceData.length === 0) {
       
        return null; // Return null to indicate no real data available
      }

      const managersWithServiceData = managersWithRealServiceData.map((mgr: any) => ({
        name: mgr.name,
        rtb: mgr.rtb || 0, // RTB HC from organization data
        tier1Service: mgr.tier1Service || 0, // Tier 1 Service count from organization data
        tier2Service: mgr.tier2Service || 0, // Tier 2 Service count from organization data
        totalTargetHC: mgr.totalTargetHC || 0,
        totalActualHC: mgr.totalActualHC || 0
      }));
      
      // Calculate aggregated metrics
      const totalRTB = managersWithServiceData.reduce((sum: number, mgr: any) => sum + mgr.rtb, 0);
      const totalTier1Services = managersWithServiceData.reduce((sum: number, mgr: any) => sum + mgr.tier1Service, 0);
      const totalTier2Services = managersWithServiceData.reduce((sum: number, mgr: any) => sum + mgr.tier2Service, 0);
      const totalSDE = managersWithServiceData.reduce((sum: number, mgr: any) => sum + (mgr.actualSDE || 0), 0);
      
      // RTB to Tier 1 Service Ratio (corrected ratio calculation)
      const rtbToTier1Ratio = totalTier1Services > 0 ? totalRTB / totalTier1Services : 0;
      
      // RTB to Total Services Ratio (Tier 1 + Tier 2)
      const rtbToTotalServicesRatio = (totalTier1Services + totalTier2Services) > 0 ? totalRTB / (totalTier1Services + totalTier2Services) : 0;
      
                      // Calculate RTB Ratio for each L7 manager (RTB/Tier1Service ratio)
                      const l7KTLORData = managersWithServiceData.map((mgr: any) => {
                        const ktlor = mgr.tier1Service > 0 ? mgr.rtb / mgr.tier1Service : 0;
                        let healthStatus: 'healthy' | 'warning' | 'critical';
                        
                        if (ktlor < 0.4) healthStatus = 'healthy';
                        else if (ktlor <= 0.5) healthStatus = 'warning';
                        else healthStatus = 'critical';
                        
                        return {
                          ...mgr,
                          ktlor,
                          healthStatus
                        };
                      }).sort((a: any, b: any) => b.ktlor - a.ktlor); // Sort by RTB Ratio descending

    

      return {
        totalRTB,
        totalTier1Services,
        totalTier2Services,
        totalSDE,
        rtbToTier1Ratio,
        rtbToTotalServicesRatio,
        l7KTLORData,
        serviceDistributionData: [
          { name: 'Tier 1 Services', value: totalTier1Services, fill: COLORS[0] },
          { name: 'Tier 2 Services', value: totalTier2Services, fill: COLORS[1] }
        ]
      };
    } else {
      
      return null; // Return null when no real data is available
    }
  }, [filters.l7_managers, organizationData]);

  // Running the Business breakdown data
  const runningBusinessData = useMemo(() => {
    const rtbCategories = new Map<string, any>();
    
    // Define RTB categories based on themes (removed Safety and Compliance as it's not KTLO)
    const rtbThemeMapping: Record<string, string> = {
      'Running the Business (Overhead, KTLO, KTLOR, Mandates, Availability and Resiliency)': 'KTLO & Overhead',
      'KTLO': 'KTLO & Overhead',
      'KTLOR': 'KTLO & Overhead',
      'L7 OH': 'Overhead',
      'Mandates': 'Mandates',
      'Legal Mandates': 'Mandates',
      'Resiliency': 'Resiliency',
      'Resiliency and Re-architecture': 'Resiliency',
      'Overhead': 'Overhead',
      'Fixed Field Overhead': 'Overhead'
    };
    
    // Use filteredTasks to respect the filters at the top
     
    
    // Instead of categorizing by themes, show RTB distribution by programs
    filteredTasks.forEach(task => {
      const theme = (task as any).theme || '';
      const program = (task as any).program || '';
      const ent = parseFloat((task as any).annualized_entitlement) || 0;
      const sde = parseFloat((task as any).hc_sde) || 0;
      
      // Check if this is a Running the Business task - based purely on theme
      let isRTB = false;
      
      // Check theme mapping - this is the only criteria for RTB classification
      if (rtbThemeMapping[theme]) {
        isRTB = true;
      
      }
      // Check for KTLO in program name as secondary criteria
      else if (/KTLO/i.test(program)) {
        isRTB = true;
       
      }
      
      // If this is an RTB task, categorize by program instead of theme
      if (isRTB && program) {
        const programName = program || 'Unassigned RTB Program';
        
        if (!rtbCategories.has(programName)) {
          rtbCategories.set(programName, {
            name: programName,
            entitlement: 0, // RTB tasks have no entitlement
            sde: 0,
            count: 0,
            source: 'tasks',
            program: programName
          });
        }
        
        const current = rtbCategories.get(programName)!;
        current.sde += sde; // Focus on SDE HC instead of entitlement
        current.count += 1;
      }
    });
    
    const result = Array.from(rtbCategories.values())
      .filter(d => d.sde > 0) // Filter by SDE instead of entitlement
      .sort((a, b) => b.sde - a.sde); // Sort by SDE instead of entitlement
    
     
    
    return result;
  }, [filteredTasks, filters.l7_managers]);

  const chartData = useMemo(() => {
    if (activeView === 'program') return byProgram.sort((a, b) => b.totalEnt - a.totalEnt).slice(0, 20);
    if (activeView === 'l7') return byL7.sort((a, b) => b.totalEnt - a.totalEnt).slice(0, 20);
    if (activeView === 'theme') return byTheme.sort((a, b) => b.totalEnt - a.totalEnt).slice(0, 20);
    return byProgram.slice(0, 20);
  }, [activeView, byProgram, byL7, byTheme]);

  const clearFilters = () => {
    setFilters({
      search: '',
      l7_managers: [],
      programs: [],
      op_decisions: [],
      priorities: [],
      mission_fits: [],
      impact_metrics: [],
      target_years: [],
      request_types: [],
    });
  };

  const hasActiveFilters = useMemo(() => {
    return filters.search || 
           filters.l7_managers.length > 0 || 
           filters.programs.length > 0 || 
           filters.op_decisions.length > 0 || 
           filters.priorities.length > 0 || 
           filters.mission_fits.length > 0 || 
           filters.impact_metrics.length > 0 ||
           filters.target_years.length > 0 ||
           filters.request_types.length > 0;
  }, [filters]);

  const removeFilter = (type: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [type]: (prev[type as keyof typeof prev] as string[]).filter(v => v !== value),
    }));
  };

  if (isLoading) return <div className="p-6">Loading analytics...</div>;

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="analytics-header-content">
          <div className="analytics-header-row">
            <div className="analytics-header-left">
              <div>
                <h1 className="analytics-title">Roadmap Analytics</h1>
                <p className="analytics-subtitle">Real-time insights into entitlement, capacity & portfolio health</p>
              </div>
              <div className="header-buttons">
                <button
                  onClick={() => navigate(-1)}
                  className="back-button"
                >
                  ← Dashboard
                </button>
                <button
                  onClick={() => navigate(`/admin/product/${orgId}`)}
                  className="back-button"
                >
                  ← Back to Planning
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="filter-toggle-button"
            >
              <Filter size={16} />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-cards-container">
          <div className="kpi-cards-grid">
            <div className="kpi-card kpi-card-gray">
              <div className="kpi-card-label text-gray-700">Year</div>
              <div className="kpi-card-value text-gray-900">
                {filters.target_years.length === 0 
                  ? 'All Years' 
                  : filters.target_years.length === 1 
                    ? filters.target_years[0] 
                    : `${filters.target_years.length} Selected`
                }
              </div>
              <div className="kpi-card-subtitle text-gray-600">Target year filter</div>
            </div>
            
            <div className="kpi-card kpi-card-gray">
              <div className="kpi-card-label text-gray-700">Request Type</div>
              <div className="kpi-card-value text-gray-900">
                {filters.request_types.length === 0 
                  ? 'All Types' 
                  : filters.request_types.length === 1 
                    ? filters.request_types[0] 
                    : `${filters.request_types.length} Selected`
                }
              </div>
              <div className="kpi-card-subtitle text-gray-600">Request type filter</div>
            </div>
            
            <div className="kpi-card kpi-card-blue">
              <div className="kpi-card-label text-blue-700">Total Entitlement</div>
              <div className="kpi-card-value text-blue-900">{formatCompactCurrency(metrics.totalEnt)}</div>
              <div className="kpi-card-subtitle text-blue-600">Avg: {formatCompactCurrency(metrics.avgEntPerTask)}/task</div>
            </div>
            
            <div className="kpi-card kpi-card-green">
              <div className="kpi-card-label text-green-700">Total SDE HC</div>
              <div className="kpi-card-value text-green-900">{formatNumber(metrics.totalSDE)}</div>
              <div className="kpi-card-subtitle text-green-600">{formatNumber(metrics.avgSDEPerTask)} avg/task</div>
            </div>
            
            <div className="kpi-card kpi-card-purple">
              <div className="kpi-card-label text-purple-700">Requests</div>
              <div className="kpi-card-value text-purple-900">{metrics.totalTasks}</div>
              <div className="kpi-card-subtitle text-purple-600">Filtered view</div>
            </div>
            
            <div className="kpi-card kpi-card-orange">
              <div className="kpi-card-label text-orange-700">Ent / SDE</div>
              <div className="kpi-card-value text-orange-900">{formatCompactCurrency(metrics.entPerSDE)}</div>
              <div className="kpi-card-subtitle text-orange-600">Efficiency ratio</div>
            </div>
            
            <div 
              className={`kpi-card ${
                metrics.healthScore >= 70 ? 'kpi-card-green' : 
                metrics.healthScore >= 40 ? 'kpi-card-yellow' : 'kpi-card-red'
              }`}
              title={`Program Health Score: ${metrics.healthScore}/100\n\nMeasures portfolio health based on:\n• Strategic Alignment (40%): ${Math.round(metrics.strategicScore)}/100\n• Portfolio Balance (35%): ${Math.round(metrics.balanceScore)}/100\n• Mission Fit (25%): ${Math.round(metrics.missionFitScore)}/100\n\nSee detailed breakdown below for improvement recommendations.`}
            >
              <div className={`kpi-card-label ${
                metrics.healthScore >= 70 ? 'text-green-700' : 
                metrics.healthScore >= 40 ? 'text-yellow-700' : 'text-red-700'
              }`}>Health Score</div>
              <div className={`kpi-card-value ${
                metrics.healthScore >= 70 ? 'text-green-900' : 
                metrics.healthScore >= 40 ? 'text-yellow-900' : 'text-red-900'
              }`}>{metrics.healthScore}/100</div>
              <div className={`kpi-card-health-indicator ${
                metrics.healthScore >= 70 ? 'text-green-600' : 
                metrics.healthScore >= 40 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {metrics.healthScore >= 70 ? <Check size={12} /> : <AlertTriangle size={12} />}
                {metrics.healthScore >= 70 ? 'Healthy' : metrics.healthScore >= 40 ? 'Fair' : 'Needs attention'}
              </div>
            </div>
            
            <div className="kpi-card kpi-card-purple">
              <div className="kpi-card-label text-purple-700">Mission Fit</div>
              <div className="kpi-card-value text-purple-900">{formatNumber(metrics.missionFitTasks.sde)} HC</div>
              <div className="kpi-card-subtitle text-purple-600">{formatCompactCurrency(metrics.missionFitEntitlement)} • {metrics.missionFitTasks.count} Tasks</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-content">
            <div className="filters-grid">
              <div className="filters-search-col">
                <input
                  placeholder="Search tasks, programs, themes..."
                  className="search-input"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              
              <MultiSelectDropdown
                label="Programs"
                options={programs}
                selected={filters.programs}
                onChange={(v) => setFilters(prev => ({ ...prev, programs: v }))}
              />
              
              <MultiSelectDropdown
                label="L7 SDMs"
                options={l7s}
                selected={filters.l7_managers}
                onChange={(v) => setFilters(prev => ({ ...prev, l7_managers: v }))}
              />
              
              <MultiSelectDropdown
                label="Op Decision"
                options={opDecisions}
                selected={filters.op_decisions}
                onChange={(v) => setFilters(prev => ({ ...prev, op_decisions: v }))}
              />
              
              <MultiSelectDropdown
                label="Mission Fit"
                options={missionFits}
                selected={filters.mission_fits}
                onChange={(v) => setFilters(prev => ({ ...prev, mission_fits: v }))}
              />
              
              <MultiSelectDropdown
                label="Impact Metrics"
                options={impactMetrics}
                selected={filters.impact_metrics}
                onChange={(v) => setFilters(prev => ({ ...prev, impact_metrics: v }))}
              />
              
              <MultiSelectDropdown
                label="Target Year"
                options={targetYears}
                selected={filters.target_years}
                onChange={(v) => setFilters(prev => ({ ...prev, target_years: v }))}
              />
              
              <MultiSelectDropdown
                label="Request Type"
                options={requestTypes}
                selected={filters.request_types}
                onChange={(v) => setFilters(prev => ({ ...prev, request_types: v }))}
              />
            </div>

            {/* Active Filter Tags */}
            {hasActiveFilters && (
              <div className="filter-tags">
                <span className="filter-tags-label">Active filters:</span>
                
                {filters.programs.map(p => (
                  <span key={p} className="filter-tag filter-tag-blue">
                    {p}
                    <button onClick={() => removeFilter('programs', p)}><X size={12} /></button>
                  </span>
                ))}
                
                {filters.l7_managers.map(l => (
                  <span key={l} className="filter-tag filter-tag-green">
                    {l}
                    <button onClick={() => removeFilter('l7_managers', l)}><X size={12} /></button>
                  </span>
                ))}
                
                {filters.op_decisions.map((o: string) => (
                  <span key={o} className="filter-tag filter-tag-orange">
                    {o}
                    <button onClick={() => removeFilter('op_decisions', o)}><X size={12} /></button>
                  </span>
                ))}
                
                {filters.mission_fits.map((t: string) => (
                  <span key={t} className="filter-tag filter-tag-purple">
                    {t}
                    <button onClick={() => removeFilter('mission_fits', t)}><X size={12} /></button>
                  </span>
                ))}
                
                {filters.impact_metrics.map((i: string) => (
                  <span key={i} className="filter-tag filter-tag-green">
                    {i}
                    <button onClick={() => removeFilter('impact_metrics', i)}><X size={12} /></button>
                  </span>
                ))}
                
                {filters.target_years.map((y: string) => (
                  <span key={y} className="filter-tag filter-tag-blue">
                    {y}
                    <button onClick={() => removeFilter('target_years', y)}><X size={12} /></button>
                  </span>
                ))}
                
                {filters.request_types.map((r: string) => (
                  <span key={r} className="filter-tag filter-tag-purple">
                    {r}
                    <button onClick={() => removeFilter('request_types', r)}><X size={12} /></button>
                  </span>
                ))}
                
                <button
                  onClick={clearFilters}
                  className="clear-filters-button"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* View Tabs */}
        <div className="content-section">
          <div className="view-tabs">
            <button
              onClick={() => setActiveView('program')}
              className={`view-tab ${activeView === 'program' ? 'view-tab-active' : 'view-tab-inactive'}`}
            >
              📊 By Program
            </button>
            
            <button
              onClick={() => setActiveView('l7')}
              className={`view-tab ${activeView === 'l7' ? 'view-tab-active' : 'view-tab-inactive'}`}
            >
              👥 By L7 Manager
            </button>
            
            <button
              onClick={() => setActiveView('theme')}
              className={`view-tab ${activeView === 'theme' ? 'view-tab-active' : 'view-tab-inactive'}`}
            >
              🎯 By Theme
            </button>
            
            <button
              onClick={() => setActiveView('efficiency')}
              className={`view-tab ${activeView === 'efficiency' ? 'view-tab-active' : 'view-tab-inactive'}`}
            >
              ⚡ Efficiency Analysis
            </button>
            
            <button
              onClick={() => setActiveView('operations')}
              className={`view-tab ${activeView === 'operations' ? 'view-tab-active' : 'view-tab-inactive'}`}
            >
              🔧 Operations & Technical Debt
            </button>
          </div>
        </div>

        {/* Program Health Score Breakdown - Hide on Operations tab */}
        {activeView !== 'operations' && (
          <div className="content-section">
            <div className="analytics-card">
              <h3 className="analytics-card-title">🏥 Program Health Score Breakdown</h3>
              <div className="health-score-breakdown">

                <div className="health-components">
                <div className="health-component">
                  <div className="health-component-header">
                    <span className="health-component-title">Strategic Alignment</span>
                    <span className="health-component-weight">(40% weight)</span>
                    <span className="health-component-score">{Math.round(metrics.strategicScore)}/100</span>
                  </div>
                  <div className="health-component-bar">
                    <div 
                      className="health-component-fill health-component-fill-strategic"
                      style={{ width: `${metrics.strategicScore}%` }}
                    ></div>
                  </div>
                  <div className="health-component-details">
                    <div className="health-detail-item">
                      <span>{hasOpDecisionFilter ? 'P1 Tasks:' : 'P1 Programs:'}</span>
                      <span>
                        {hasOpDecisionFilter ? 
                          `${filteredTasks.filter(t => /P1/i.test((t as any).op_decision || '')).length} tasks (${formatCompactCurrency(metrics.p1Programs.ent)})` :
                          `${metrics.p1Programs.count} programs (${formatCompactCurrency(metrics.p1Programs.ent)})`
                        }
                      </span>
                    </div>
                    <div className="health-detail-item">
                      <span>{hasOpDecisionFilter ? 'P2 Tasks:' : 'P2 Programs:'}</span>
                      <span>
                        {hasOpDecisionFilter ? 
                          `${filteredTasks.filter(t => /P2/i.test((t as any).op_decision || '')).length} tasks (${formatCompactCurrency(metrics.p2Programs.ent)})` :
                          `${metrics.p2Programs.count} programs (${formatCompactCurrency(metrics.p2Programs.ent)})`
                        }
                      </span>
                    </div>
                    <div className="health-detail-recommendation">
                      {metrics.strategicScore >= 75 ? 
                        '✅ Excellent P1/P2 balance' :
                        metrics.strategicScore >= 60 ?
                        '⚠️ Good P1 focus, move more to P1' :
                        '🚨 Too much P2 investment'}
                    </div>
                  </div>
                </div>

                <div className="health-component">
                  <div className="health-component-header">
                    <span className="health-component-title">Portfolio Balance</span>
                    <span className="health-component-weight">(35% weight)</span>
                    <span className="health-component-score">{Math.round(metrics.balanceScore)}/100</span>
                  </div>
                  <div className="health-component-bar">
                    <div 
                      className="health-component-fill health-component-fill-balance"
                      style={{ width: `${metrics.balanceScore}%` }}
                    ></div>
                  </div>
                  <div className="health-component-details">
                    <div className="health-detail-item">
                      <span>Total Programs:</span>
                      <span>{metrics.programCount} programs</span>
                    </div>
                    <div className="health-detail-item">
                      <span>Avg Size:</span>
                      <span>{formatNumber(metrics.avgProgramSize)} SDE (ideal: 5-15)</span>
                    </div>
                    <div className="health-detail-recommendation">
                      {metrics.balanceScore >= 80 ? 
                        '✅ Well-balanced portfolio' :
                        metrics.avgProgramSize < 5 ?
                        '⚠️ Consider consolidating programs' :
                        '⚠️ Consider breaking down large programs'}
                    </div>
                  </div>
                </div>

                <div className="health-component">
                  <div className="health-component-header">
                    <span className="health-component-title">Mission Fit</span>
                    <span className="health-component-weight">(25% weight)</span>
                    <span className="health-component-score">{Math.round(metrics.missionFitScore)}/100</span>
                  </div>
                  <div className="health-component-bar">
                    <div 
                      className="health-component-fill health-component-fill-execution"
                      style={{ width: `${metrics.missionFitScore}%` }}
                    ></div>
                  </div>
                  <div className="health-component-details">
                    <div className="health-detail-item">
                      <span>Mission Fit Tasks:</span>
                      <span>{metrics.missionFitTasks.count} tasks</span>
                    </div>
                    <div className="health-detail-item">
                      <span>Mission Fit Entitlement:</span>
                      <span>{formatCompactCurrency(metrics.missionFitEntitlement)} ({Math.round(metrics.missionFitRatio * 100)}%)</span>
                    </div>
                    <div className="health-detail-recommendation">
                      {metrics.missionFitScore >= 90 ? 
                        '✅ Excellent mission alignment' :
                        metrics.missionFitScore >= 70 ?
                        '⚠️ Good mission fit, room for improvement' :
                        '🚨 Low mission fit - needs attention'}
                    </div>
                  </div>
                </div>

              </div>

              <div className="program-classification">
                <h4 className="program-classification-title">
                  {hasOpDecisionFilter ? 'Strategic Alignment (Filtered View)' : 'Program Strategic Classification'}
                </h4>
                {hasOpDecisionFilter && (
                  <div className="program-classification-note">
                    <small>Showing entitlement breakdown based on selected Op Decision filters</small>
                  </div>
                )}
                <div className="program-classification-grid">
                  <div className="program-class-card program-class-p1">
                    <div className="program-class-header">
                      <span className="program-class-label">
                        {hasOpDecisionFilter ? 'P1 Tasks' : 'P1 Programs'}
                      </span>
                      <span className="program-class-count">
                        {hasOpDecisionFilter ? 
                          filteredTasks.filter(t => /P1/i.test((t as any).op_decision || '')).length :
                          metrics.p1Programs.count
                        }
                      </span>
                    </div>
                    <div className="program-class-details">
                      <div>Entitlement: {formatCompactCurrency(metrics.p1Programs.ent)}</div>
                      <div>
                        {hasOpDecisionFilter ? 
                          `Tasks: ${filteredTasks.filter(t => /P1/i.test((t as any).op_decision || '')).length}` :
                          `Avg Size: ${metrics.p1Programs.count > 0 ? formatNumber(metrics.p1Programs.programs.reduce((sum, p) => sum + p.totalSDE, 0) / metrics.p1Programs.count) : '0'} SDE`
                        }
                      </div>
                    </div>
                    <div className="program-class-programs">
                      {!hasOpDecisionFilter ? (
                        <>
                          {metrics.p1Programs.programs.slice(0, 3).map(p => (
                            <div key={p.name} className="program-class-program">
                              <div className="program-name">{p.name}</div>
                              <div className="program-details">
                                {p.taskCount} tasks • {formatCompactCurrency(p.totalEnt)} ent
                              </div>
                            </div>
                          ))}
                          {metrics.p1Programs.programs.length > 3 && (
                            <div className="program-class-more">+{metrics.p1Programs.programs.length - 3} more programs</div>
                          )}
                        </>
                      ) : (
                        <div className="program-class-program">
                          {filteredTasks.filter(t => /P1/i.test((t as any).op_decision || '')).length > 0 ? 
                            'P1 - Critical tasks' : 'No P1 tasks in filter'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="program-class-card program-class-p2">
                    <div className="program-class-header">
                      <span className="program-class-label">
                        {hasOpDecisionFilter ? 'P2 Tasks' : 'P2 Programs'}
                      </span>
                      <span className="program-class-count">
                        {hasOpDecisionFilter ? 
                          filteredTasks.filter(t => /P2/i.test((t as any).op_decision || '')).length :
                          metrics.p2Programs.count
                        }
                      </span>
                    </div>
                    <div className="program-class-details">
                      <div>Entitlement: {formatCompactCurrency(metrics.p2Programs.ent)}</div>
                      <div>
                        {hasOpDecisionFilter ? 
                          `Tasks: ${filteredTasks.filter(t => /P2/i.test((t as any).op_decision || '')).length}` :
                          `Avg Size: ${metrics.p2Programs.count > 0 ? formatNumber(metrics.p2Programs.programs.reduce((sum, p) => sum + p.totalSDE, 0) / metrics.p2Programs.count) : '0'} SDE`
                        }
                      </div>
                    </div>
                    <div className="program-class-programs">
                      {!hasOpDecisionFilter ? (
                        <>
                          {metrics.p2Programs.programs.slice(0, 2).map(p => (
                            <div key={p.name} className="program-class-program">{p.name}</div>
                          ))}
                          {metrics.p2Programs.programs.length > 2 && (
                            <div className="program-class-more">+{metrics.p2Programs.programs.length - 2} more</div>
                          )}
                        </>
                      ) : (
                        <div className="program-class-program">
                          {filteredTasks.filter(t => /P2/i.test((t as any).op_decision || '')).length > 0 ? 
                            'P2 - Important tasks' : 'No P2 tasks in filter'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="program-class-card program-class-btl">
                    <div className="program-class-header">
                      <span className="program-class-label">
                        {hasOpDecisionFilter ? 'BTL Tasks' : 'BTL Programs'}
                      </span>
                      <span className="program-class-count">
                        {hasOpDecisionFilter ? 
                          filteredTasks.filter(t => /BTL|P3/i.test((t as any).op_decision || '')).length :
                          metrics.btlPrograms.count
                        }
                      </span>
                    </div>
                    <div className="program-class-details">
                      <div>Entitlement: {formatCompactCurrency(metrics.btlPrograms.ent)}</div>
                      <div>
                        {hasOpDecisionFilter ? 
                          `Tasks: ${filteredTasks.filter(t => /BTL|P3/i.test((t as any).op_decision || '')).length}` :
                          `Avg Size: ${metrics.btlPrograms.count > 0 ? formatNumber(metrics.btlPrograms.programs.reduce((sum, p) => sum + p.totalSDE, 0) / metrics.btlPrograms.count) : '0'} SDE`
                        }
                      </div>
                    </div>
                    <div className="program-class-programs">
                      {!hasOpDecisionFilter ? (
                        <>
                          {metrics.btlPrograms.programs.slice(0, 2).map(p => (
                            <div key={p.name} className="program-class-program">{p.name}</div>
                          ))}
                          {metrics.btlPrograms.programs.length > 2 && (
                            <div className="program-class-more">+{metrics.btlPrograms.programs.length - 2} more</div>
                          )}
                        </>
                      ) : (
                        <div className="program-class-program">
                          {filters.op_decisions.some((f: string) => /BTL|P3/i.test(f)) ? 'BTL/P3 tasks' : 'No BTL tasks in filter'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Portfolio Health Overview - Hide on Operations tab */}
        {activeView !== 'operations' && (
        <div className="content-section">
          <div className="analytics-grid">
            <div className="analytics-card">
              <h3 className="analytics-card-title">Portfolio Balance</h3>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={portfolioData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry: any) => 
                      `${entry.name.split(' ')[0]}: ${Math.round((entry.value / metrics.totalSDE) * 100)}%`
                    }
                  >
                    {portfolioData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                </PieChart>
              </ResponsiveContainer>
              <div className="legend-container">
                {portfolioData.map(d => (
                  <div key={d.name} className="legend-item">
                    <span className="legend-item-left">
                      <div className="legend-color" style={{ backgroundColor: d.fill }}></div>
                      {d.name}
                    </span>
                    <span className="legend-item-right">{formatNumber(d.value)} SDE</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="analytics-card">
              <h3 className="analytics-card-title">Program Balance Radar</h3>
              <ResponsiveContainer width="100%" height={360}>
                <RadarChart data={radarData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="program" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 'auto']} tick={{ fontSize: 10 }} />
                  <Radar name="SDE HC" dataKey="sde" stroke={COLORS[1]} fill={COLORS[1]} fillOpacity={0.3} strokeWidth={2} />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="analytics-card">
              <h3 className="analytics-card-title">Entitlement by Impact Metrics</h3>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={impactMetricsData} margin={{ top: 20, right: 20, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    interval={0} 
                    height={100} 
                    tick={{ fontSize: 11 }} 
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value: any, name: string) => {
                      if (name === 'Entitlement') return formatCompactCurrency(Number(value));
                      return formatNumber(Number(value));
                    }}
                    labelFormatter={(label) => `Impact Metric: ${label}`}
                  />
                  <Legend />
                  <Bar 
                    dataKey="entitlement" 
                    name="Entitlement" 
                    fill={COLORS[4]} 
                    radius={[4, 4, 0, 0]} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        )}

        {/* Operations & Technical Debt View */}
        {activeView === 'operations' && (
          <>
            {serviceOperationsData ? (
              <>
                {/* Service Operations Analysis */}
                <div className="content-section">
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <h3 className="analytics-card-title">🔧 Service Operations Analysis</h3>
                      <button
                        onClick={() => downloadCSV('service-operations-export.csv', [
                          {
                            metric: 'Total Tier 1 Services',
                            value: serviceOperationsData.totalTier1Services,
                            description: 'Critical services'
                          },
                          {
                            metric: 'Total Tier 2 Services', 
                            value: serviceOperationsData.totalTier2Services,
                            description: 'Standard services'
                          },
                          {
                            metric: 'RTB HC',
                            value: serviceOperationsData.totalRTB,
                            description: 'Running the business headcount'
                          },
                          {
                            metric: 'RTB to Tier 1 Ratio',
                            value: serviceOperationsData.rtbToTier1Ratio,
                            description: 'Technical debt indicator'
                          },
                          {
                            metric: 'RTB to Tier 1+2 Ratio',
                            value: serviceOperationsData.rtbToTotalServicesRatio,
                            description: 'All services ratio'
                          },
                          ...serviceOperationsData.l7KTLORData.map((mgr: any) => ({
                            l7_manager: mgr.name,
                            rtb_hc: mgr.rtb,
                            tier1_services: mgr.tier1Service,
                            tier2_services: mgr.tier2Service,
                            ktlor: mgr.ktlor,
                            health_status: mgr.healthStatus,
                            total_hc: mgr.totalActualHC
                          }))
                        ], setIsExporting)}
                        className="export-button"
                        disabled={isExporting}
                      >
                        {isExporting ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid currentColor',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              marginRight: '6px'
                            }}></div>
                            Exporting...
                          </>
                        ) : (
                          <>
                            <Download size={14} /> Export Operations Data
                          </>
                        )}
                      </button>
                    </div>
                    <div className="service-operations-summary">
                      <div className="service-operations-stats">
                        <div className="service-stat">
                          <div className="service-stat-label">Total Tier 1 Services</div>
                          <div className="service-stat-value">{serviceOperationsData.totalTier1Services}</div>
                          <div className="service-stat-subtitle">Critical services</div>
                        </div>
                        <div className="service-stat">
                          <div className="service-stat-label">Total Tier 2 Services</div>
                          <div className="service-stat-value">{serviceOperationsData.totalTier2Services}</div>
                          <div className="service-stat-subtitle">Standard services</div>
                        </div>
                        <div className="service-stat">
                          <div className="service-stat-label">RTB HC</div>
                          <div className="service-stat-value">{serviceOperationsData.totalRTB}</div>
                          <div className="service-stat-subtitle">Running the business</div>
                        </div>
                        <div className="service-stat">
                          <div className="service-stat-label">Total SDE HC</div>
                          <div className="service-stat-value">{serviceOperationsData.totalSDE}</div>
                          <div className="service-stat-subtitle">From organization data</div>
                        </div>
                        <div className={`service-stat ${
                          serviceOperationsData.rtbToTier1Ratio < 0.4 ? 'service-stat-healthy' :
                          serviceOperationsData.rtbToTier1Ratio <= 0.5 ? 'service-stat-warning' : 'service-stat-critical'
                        }`}>
                          <div className="service-stat-label">RTB to Tier 1 Ratio</div>
                          <div className="service-stat-value">{formatNumber(serviceOperationsData.rtbToTier1Ratio)}</div>
                          <div className="service-stat-subtitle">
                            {serviceOperationsData.rtbToTier1Ratio < 0.4 ? '✅ Healthy' :
                             serviceOperationsData.rtbToTier1Ratio <= 0.5 ? '⚠️ Monitor' : '🚨 Critical'}
                          </div>
                        </div>
                        <div className="service-stat">
                          <div className="service-stat-label">RTB to Tier 1+2 Ratio</div>
                          <div className="service-stat-value">{formatNumber(serviceOperationsData.rtbToTotalServicesRatio)}</div>
                          <div className="service-stat-subtitle">All services ratio</div>
                        </div>
                      </div>
                    </div>

                    <div className="analytics-grid">
                      <div className="analytics-card">
                        <h4 className="analytics-card-title">Service Distribution</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={serviceOperationsData.serviceDistributionData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={(entry: any) => `${entry.name}: ${entry.value}`}
                            >
                              {serviceOperationsData.serviceDistributionData.map((entry, index) => (
                                <Cell key={`service-cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="analytics-card">
                        <h4 className="analytics-card-title">Service Operations KPIs</h4>
                        <div className="service-kpis">
                          <div className="service-kpi">
                            <div className="service-kpi-label">Total Services</div>
                            <div className="service-kpi-value">
                              {serviceOperationsData.totalTier1Services + serviceOperationsData.totalTier2Services}
                            </div>
                          </div>
                          <div className="service-kpi">
                            <div className="service-kpi-label">Tier 1 Ratio</div>
                            <div className="service-kpi-value">
                              {serviceOperationsData.totalTier1Services + serviceOperationsData.totalTier2Services > 0 ? 
                                Math.round((serviceOperationsData.totalTier1Services / (serviceOperationsData.totalTier1Services + serviceOperationsData.totalTier2Services)) * 100) : 0}%
                            </div>
                          </div>
                          <div className="service-kpi">
                            <div className="service-kpi-label">RTB per Service</div>
                            <div className="service-kpi-value">
                              {formatNumber(serviceOperationsData.totalTier1Services + serviceOperationsData.totalTier2Services > 0 ? 
                                serviceOperationsData.totalRTB / (serviceOperationsData.totalTier1Services + serviceOperationsData.totalTier2Services) : 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RTB Ratio Analysis */}
                <div className="content-section">
                  <div className="analytics-card">
                    <h3 className="analytics-card-title">📊 RTB Ratio (Running The Business Ratio) Analysis</h3>
                    <div className="ktlor-analysis">
                      <div className="ktlor-summary">
                        <p>RTB Ratio measures technical debt burden by comparing RTB headcount to Tier 1 services. Lower ratios indicate better operational efficiency.</p>
                        <div className="ktlor-thresholds">
                          <span className="ktlor-threshold ktlor-healthy">&lt; 0.4 Healthy</span>
                          <span className="ktlor-threshold ktlor-warning">0.4-0.5 Monitor</span>
                          <span className="ktlor-threshold ktlor-critical">&gt; 0.5 Critical</span>
                        </div>
                      </div>

                      <div className="analytics-grid">
                        <div className="analytics-card">
                          <h4 className="analytics-card-title">L7 Manager RTB Ratio Comparison</h4>
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart 
                              data={serviceOperationsData.l7KTLORData} 
                              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="name" 
                                angle={-45} 
                                textAnchor="end" 
                                interval={0} 
                                height={100} 
                                tick={{ fontSize: 10 }} 
                              />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip 
                                formatter={(value: any) => [formatNumber(Number(value)), 'RTB Ratio']}
                                labelFormatter={(label) => `L7 Manager: ${label}`}
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '0.375rem',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                              <Bar 
                                dataKey="ktlor" 
                                name="RTB Ratio"
                                radius={[4, 4, 0, 0]}
                              >
                                {serviceOperationsData.l7KTLORData.map((entry: any, index: number) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={
                                      entry.ktlor < 0.4 ? '#10B981' : // Green - Healthy
                                      entry.ktlor <= 0.5 ? '#F59E0B' : // Orange - Warning  
                                      '#EF4444' // Red - Critical
                                    } 
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                          <div className="rtb-chart-legend">
                            <div className="rtb-legend-item">
                              <div className="rtb-legend-color" style={{ backgroundColor: '#10B981' }}></div>
                              <span>Healthy (&lt; 0.4)</span>
                            </div>
                            <div className="rtb-legend-item">
                              <div className="rtb-legend-color" style={{ backgroundColor: '#F59E0B' }}></div>
                              <span>Monitor (0.4-0.5)</span>
                            </div>
                            <div className="rtb-legend-item">
                              <div className="rtb-legend-color" style={{ backgroundColor: '#EF4444' }}></div>
                              <span>Critical (&gt; 0.5)</span>
                            </div>
                          </div>
                        </div>

                        <div className="analytics-card">
                          <h4 className="analytics-card-title">Technical Debt Hotspots</h4>
                          <div className="ktlor-details">
                            {serviceOperationsData.l7KTLORData.map((mgr: any, index: number) => (
                              <div key={mgr.name} className={`ktlor-manager-card ktlor-${mgr.healthStatus}`}>
                                <div className="ktlor-manager-header">
                                  <div className="ktlor-manager-name">{mgr.name}</div>
                                  <div className={`ktlor-health-badge ktlor-health-${mgr.healthStatus}`}>
                                    {mgr.healthStatus === 'healthy' ? '✅' : mgr.healthStatus === 'warning' ? '⚠️' : '🚨'}
                                    {formatNumber(mgr.ktlor)}
                                  </div>
                                </div>
                                <div className="ktlor-manager-details">
                                  <div className="ktlor-detail">
                                    <span>RTB HC:</span>
                                    <span>{mgr.rtb}</span>
                                  </div>
                                  <div className="ktlor-detail">
                                    <span>Tier 1 Services:</span>
                                    <span>{mgr.tier1Service}</span>
                                  </div>
                                  <div className="ktlor-detail">
                                    <span>Tier 2 Services:</span>
                                    <span>{mgr.tier2Service}</span>
                                  </div>
                                  <div className="ktlor-detail">
                                    <span>Total HC:</span>
                                    <span>{mgr.totalActualHC}</span>
                                  </div>
                                </div>
                                <div className="ktlor-recommendation">
                                  {mgr.healthStatus === 'healthy' ? 
                                    '✅ Optimal technical debt management' :
                                    mgr.healthStatus === 'warning' ?
                                    '⚠️ Monitor for service consolidation opportunities' :
                                    '🚨 High technical debt - consider service architecture review'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="content-section">
                <div className="analytics-card">
                  <h3 className="analytics-card-title">🔧 Service Operations Analysis</h3>
                  <div className="no-data-message">
                    <div className="no-data-icon">📊</div>
                    <h4>No Service Operations Data Available</h4>
                    <p>
                      Service operations analysis requires real organization data with L7 manager service information.
                      The Technical Debt Hotspots section only displays data when:
                    </p>
                    <ul>
                      <li>Organization data is loaded from S3</li>
                      <li>L7 managers have actual Tier 1 and Tier 2 service counts</li>
                      <li>RTB (Running the Business) headcount data is available</li>
                    </ul>
                    <p>
                      Please ensure your organization data includes service counts for L7 managers, 
                      or contact your administrator to configure the organization data properly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Running the Business (RTB) Analysis - Moved from main content */}
            <div className="content-section">
              <div className="analytics-card">
                <h3 className="analytics-card-title">🏭 Running the Business (RTB) Investment Breakdown</h3>
                <div className="rtb-analysis">
                  <div className="rtb-summary">
                    <div className="rtb-summary-stats">
                      <div className="rtb-stat">
                        <div className="rtb-stat-label">Total RTB HC</div>
                        <div className="rtb-stat-value">
                          {formatNumber(runningBusinessData.reduce((sum, d) => sum + d.sde, 0))} SDE
                        </div>
                        <div className="rtb-stat-subtitle">
                          {runningBusinessData.reduce((sum, d) => sum + d.count, 0)} tasks/categories
                        </div>
                      </div>
                      <div className="rtb-stat">
                        <div className="rtb-stat-label">RTB Categories</div>
                        <div className="rtb-stat-value">{runningBusinessData.length}</div>
                        <div className="rtb-stat-subtitle">Active RTB areas</div>
                      </div>
                      <div className="rtb-stat">
                        <div className="rtb-stat-label">% of Total HC</div>
                        <div className="rtb-stat-value">
                          {metrics.totalSDE > 0 ? 
                            Math.round((runningBusinessData.reduce((sum, d) => sum + d.sde, 0) / metrics.totalSDE) * 100) : 0}%
                        </div>
                        <div className="rtb-stat-subtitle">RTB vs Innovation HC</div>
                      </div>
                    </div>
                  </div>

                  {runningBusinessData.length > 0 ? (
                    <>
                      <div className="analytics-grid">
                        <div className="analytics-card">
                          <h4 className="analytics-card-title">RTB HC Distribution</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={runningBusinessData}
                                dataKey="sde"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={(entry: any) => 
                                  `${entry.name}: ${formatNumber(entry.sde)} SDE`
                                }
                              >
                                {runningBusinessData.map((entry, index) => (
                                  <Cell key={`rtb-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(v: any) => `${formatNumber(Number(v))} SDE`} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>

                        <div className="analytics-card">
                          <h4 className="analytics-card-title">RTB Category Breakdown</h4>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={runningBusinessData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="name" 
                                angle={-45} 
                                textAnchor="end" 
                                interval={0} 
                                height={80} 
                                tick={{ fontSize: 10 }} 
                              />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip 
                                formatter={(value: any, name: string) => {
                                  if (name === 'SDE HC') return `${formatNumber(Number(value))} SDE`;
                                  return Number(value);
                                }}
                              />
                              <Legend />
                              <Bar dataKey="sde" name="SDE HC" fill={COLORS[7]} radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="rtb-details">
                        <h4 className="rtb-details-title">RTB Category Details</h4>
                        <div className="rtb-categories-grid">
                          {runningBusinessData.map((category, index) => (
                            <div key={category.name} className="rtb-category-card">
                              <div className="rtb-category-header">
                                <div 
                                  className="rtb-category-color" 
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                ></div>
                                <div className="rtb-category-name">{category.name}</div>
                              </div>
                              <div className="rtb-category-metrics">
                                <div className="rtb-category-metric">
                                  <span className="rtb-metric-label">SDE HC:</span>
                                  <span className="rtb-metric-value">{formatNumber(category.sde)}</span>
                                </div>
                                <div className="rtb-category-metric">
                                  <span className="rtb-metric-label">Tasks/Items:</span>
                                  <span className="rtb-metric-value">{category.count}</span>
                                </div>
                                <div className="rtb-category-metric">
                                  <span className="rtb-metric-label">Source:</span>
                                  <span className="rtb-metric-value">{category.source === 'organization' ? 'Organization' : 'Task-based'}</span>
                                </div>
                                <div className="rtb-category-metric">
                                  <span className="rtb-metric-label">% of RTB HC:</span>
                                  <span className="rtb-metric-value">
                                    {runningBusinessData.reduce((sum, d) => sum + d.sde, 0) > 0 ? 
                                      Math.round((category.sde / runningBusinessData.reduce((sum, d) => sum + d.sde, 0)) * 100) : 0}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="rtb-no-data">
                      <p>No Running the Business tasks found in current filter selection.</p>
                      <p>RTB tasks include themes like: KTLO, Resiliency, Mandates, Regulatory, Compliance, Safety, and Overhead.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main Analytics View */}
        {activeView !== 'efficiency' && activeView !== 'operations' && (
          <div className="content-section">
            <div className="analytics-grid">
              {/* Main Chart */}
              <div className="analytics-card analytics-card-large">
                <div className="analytics-card-header">
                  <h3 className="analytics-card-title">
                    Top {activeView === 'program' ? 'Programs' : activeView === 'l7' ? 'L7 Managers' : 'Themes'} by Entitlement
                  </h3>
                  <button
                    onClick={() => downloadCSV(`${activeView}-breakdown.csv`, chartData, setIsExporting)}
                    className="export-button"
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid currentColor',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '6px'
                        }}></div>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download size={14} /> Export
                      </>
                    )}
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 48 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="key" angle={-35} textAnchor="end" interval={0} height={80} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: any) => formatCompactCurrency(Number(v))} />
                    <Legend />
                    <Bar dataKey="totalEnt" name="Total Entitlement" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="entitlementPerSDE" name="Ent per SDE" fill={COLORS[2]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Leaderboard & Insights */}
              <div className="analytics-card">
                <div>
                  <h3 className="analytics-card-title">🏆 Top Contributors</h3>
                  <div className="leaderboard-container">
                    {chartData.slice(0, 10).map((r, idx) => (
                      <div key={r.key} className="leaderboard-item">
                        <div className="leaderboard-item-left">
                          <span className={`leaderboard-rank ${
                            idx === 0 ? 'leaderboard-rank-1' : 
                            idx === 1 ? 'leaderboard-rank-2' : 
                            idx === 2 ? 'leaderboard-rank-3' : 'leaderboard-rank-other'
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="leaderboard-name">{r.key}</span>
                        </div>
                        <div className="leaderboard-item-right">
                          <div className="leaderboard-value">{formatCompactCurrency(r.totalEnt)}</div>
                          <div className="leaderboard-subtitle">{r.count} tasks</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="insights-section">
                  <h3 className="insights-title">📈 Key Insights</h3>
                  <div className="insights-container">
                    <div className="insight-card insight-card-blue">
                      <div className="insight-content">
                        <TrendingUp size={16} className="insight-icon insight-icon-blue" />
                        <div>
                          <div className="insight-text-title insight-text-title-blue">Highest ROI</div>
                          <div className="insight-text-subtitle insight-text-subtitle-blue">
                            {chartData[0]?.key} leads with {formatCompactCurrency(chartData[0]?.entitlementPerSDE || 0)}/SDE
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`insight-card ${metrics.p1.pct > 30 ? 'insight-card-green' : 'insight-card-orange'}`}>
                      <div className="insight-content">
                        {metrics.p1.pct > 30 ? (
                          <Check size={16} className="insight-icon insight-icon-green" />
                        ) : (
                          <AlertTriangle size={16} className="insight-icon insight-icon-orange" />
                        )}
                        <div>
                          <div className={`insight-text-title ${metrics.p1.pct > 30 ? 'insight-text-title-green' : 'insight-text-title-orange'}`}>
                            P1 Allocation
                          </div>
                          <div className={`insight-text-subtitle ${metrics.p1.pct > 30 ? 'insight-text-subtitle-green' : 'insight-text-subtitle-orange'}`}>
                            {Math.round(metrics.p1.pct)}% of entitlement in P1 tasks
                            {metrics.p1.pct < 30 && ' - consider reprioritizing'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Efficiency Analysis View */}
        {activeView === 'efficiency' && (
          <div className="content-section">
            <div className="analytics-grid-2col">
              <div className="analytics-card">
                <h3 className="analytics-card-title">Efficiency Scatter: Entitlement vs SDE</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid />
                    <XAxis type="number" dataKey="sde" name="SDE HC" tick={{ fontSize: 11 }} />
                    <YAxis type="number" dataKey="entitlement" name="Entitlement" tick={{ fontSize: 11 }} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value: any, name: string) => {
                      if (name === 'Entitlement') return formatCompactCurrency(Number(value));
                      return formatNumber(Number(value));
                    }} />
                    <Scatter name="Tasks" data={efficiencyData} fill={COLORS[0]} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="analytics-card">
                <h3 className="analytics-card-title">Top Efficiency Tasks</h3>
                <div className="task-list-container">
                  {efficiencyData.sort((a, b) => b.efficiency - a.efficiency).slice(0, 15).map((task, idx) => (
                    <div key={idx} className="task-list-item">
                      <div className="task-list-name">{task.name}</div>
                      <div className="task-list-details">
                        {task.program} • {formatCompactCurrency(task.efficiency)}/SDE
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Table */}
        <div className="content-section">
          <div className="analytics-card">
            <div className="analytics-card-header">
              <h3 className="analytics-card-title">Task Details ({filteredTasks.length} tasks)</h3>
              <button
                onClick={() => downloadCSV('roadmap-tasks-export.csv', filteredTasks.map(t => ({
                  gid: t.gid,
                  name: t.name,
                  program: (t as any).program,
                  theme: (t as any).theme,
                  l7_manager: (t as any).l7_manager,
                  op_decision: (t as any).op_decision,
                  priority: t.priority,
                  annualized_entitlement: (t as any).annualized_entitlement,
                  hc_sde: (t as any).hc_sde,
                  impact_metrics: (t as any).impact_metrics,
                })), setIsExporting)}
                className="export-button-primary"
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      border: '2px solid currentColor',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '6px'
                    }}></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={14} /> Export All Tasks
                  </>
                )}
              </button>
            </div>
            <div className="table-container">
              <table className="analytics-table">
                <thead className="table-header">
                  <tr>
                    <th>Task</th>
                    <th>Program</th>
                    <th>Theme</th>
                    <th>L7</th>
                    <th>Op Priority</th>
                    <th>Entitlement</th>
                    <th>SDE HC</th>
                    <th>Ent/SDE</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {filteredTasks.slice(0, 100).map((t, i) => {
                    const ent = parseFloat((t as any).annualized_entitlement) || 0;
                    const sde = parseFloat((t as any).hc_sde) || 0;
                    const ratio = sde > 0 ? ent / sde : 0;
                    return (
                      <tr key={t.gid || i} className="table-row">
                        <td className="table-cell">
                          <div className="table-cell-name">{t.name}</div>
                          <div className="table-cell-id">{t.gid}</div>
                        </td>
                        <td className="table-cell">{(t as any).program || 'Unassigned'}</td>
                        <td className="table-cell">{(t as any).theme || '-'}</td>
                        <td className="table-cell">{(t as any).l7_manager || '-'}</td>
                        <td className="table-cell">
                          <span className={`table-cell-priority ${
                            /P1/i.test((t as any).op_decision || '') ? 'priority-p1' :
                            /P2/i.test((t as any).op_decision || '') ? 'priority-p2' :
                            'priority-other'
                          }`}>
                            {(t as any).op_decision || 'Not set'}
                          </span>
                        </td>
                        <td className="table-cell table-cell-right">{formatCompactCurrency(ent)}</td>
                        <td className="table-cell table-cell-right">{formatNumber(sde)}</td>
                        <td className="table-cell table-cell-right text-gray-600">{formatCompactCurrency(ratio)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredTasks.length > 100 && (
              <div className="table-pagination">
                Showing first 100 of {filteredTasks.length} tasks. Export to see all data.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapAnalytics;
